export interface ApiClientOptions {
  baseUrl: string;
  getJwt: () => string | null;
  onUnauthorized?: () => void;
}

export interface RequestOptions {
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClient {
  request: <T = unknown>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, opts?: RequestOptions) => Promise<T>;
}

export function makeApiClient(options: ApiClientOptions): ApiClient {
  return {
    async request<T = unknown>(
      method: 'GET' | 'POST' | 'PUT' | 'DELETE',
      path: string,
      opts: RequestOptions = {},
    ): Promise<T> {
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...(opts.headers ?? {}),
      };
      const jwt = options.getJwt();
      if (jwt) headers.Authorization = `Bearer ${jwt}`;

      const init: RequestInit = {
        method,
        credentials: 'include',
        headers,
      };
      if (opts.body !== undefined) {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(opts.body);
      }
      if (opts.signal) init.signal = opts.signal;

      const res = await fetch(`${options.baseUrl}${path}`, init);
      const contentType = res.headers.get('content-type') ?? '';
      const isJson = contentType.includes('application/json');
      const body: unknown = isJson ? await res.json().catch(() => null) : await res.text();
      if (!res.ok) {
        if (res.status === 401) options.onUnauthorized?.();
        throw new ApiError(res.status, body, `HTTP ${res.status} ${method} ${path}`);
      }
      return body as T;
    },
  };
}

export function redactUrl(input: string): string {
  try {
    const u = new URL(input);
    for (const k of ['token', 'mediaToken', 'sessionJwt']) {
      if (u.searchParams.has(k)) u.searchParams.set(k, '[redacted]');
    }
    return u.toString();
  } catch {
    return input;
  }
}
